import {
  PublicClientApplication as BasePublicClientApplication,
  Configuration,
  BrowserCacheLocation,
  SilentRequest,
  RedirectRequest,
  AccountInfo,
} from '@azure/msal-browser';
import { AxiosRequestConfig } from 'axios';

export interface MsalInstanceProps {
  clientId: string;
  policy: string;
}

export class MsalInstance extends BasePublicClientApplication {
  #hasAcquireTokenRedirectWithParallelRequests: boolean;
  scopes: string[];

  /**
   * https://learn.microsoft.com/en-us/azure/active-directory/develop/id-tokens
   * we are using 'aud' claim which in the doc above states to get access token you need to send client id
   * as the scope. Also we need 'knownAuthorities' as we are using custom api for authentication
   */
  constructor({ clientId, policy }: MsalInstanceProps) {
    const truktAuthAuthority = 'trukt.b2clogin.com';
    const msalAuthority = `https://${truktAuthAuthority}/trukt.onmicrosoft.com/${policy}`;

    const configs: Configuration = {
      auth: {
        clientId,
        authority: msalAuthority,
        knownAuthorities: [truktAuthAuthority],
        postLogoutRedirectUri: window.location.origin,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
      },
    };

    super(configs);

    this.scopes = [clientId];
    this.#hasAcquireTokenRedirectWithParallelRequests = false;
  }

  getSilentRequest(accountInfo: AccountInfo): SilentRequest {
    return {
      scopes: this.scopes,
      account: accountInfo,
    };
  }

  getRedirectRequest(accountInfo: AccountInfo): RedirectRequest {
    return {
      scopes: this.scopes,
      account: accountInfo,
    };
  }

  async getAccessToken(account: AccountInfo) {
    const request = this.getSilentRequest(account);
    const { accessToken, tokenType } = await this.acquireTokenSilent(request);
    return [tokenType, accessToken].join(' ');
  }

  setAccessTokenOnHeader(config: AxiosRequestConfig, accessToken: string) {
    Object.assign(config.headers ?? {}, { Authorization: accessToken });
  }

  /**
   * This function is utilized when endpoints return a 401 status code, prompting the user to re-authenticate.
   * As requests occur in parallel, there may be instances where this function is invoked multiple times.
   * To avoid duplicate function calls to acquireTokenRedirect, we initially verify if this method has already been invoked.
   * If the method has been called before, we do not take any action.
   * However, if the method has not been called previously, we set a flag to true and initiate the redirect.
   */
  acquireTokenRedirectWithParallelRequests(accountInfo: AccountInfo) {
    if (!this.#hasAcquireTokenRedirectWithParallelRequests) {
      this.#hasAcquireTokenRedirectWithParallelRequests = true;
      this.acquireTokenRedirect(this.getRedirectRequest(accountInfo));
    }
  }

  getHasAcquireTokenRedirectWithParallelRequests() {
    return this.#hasAcquireTokenRedirectWithParallelRequests;
  }
}
